import { useRequest } from "@certn/api";
import {
  DropdownMenu,
  GhostIconButton,
  MenuItem,
  showToast,
} from "@certn/cascadia-react";
import { t } from "i18next";
import React from "react";

import {
  downloadAllDocuments,
  downloadCSVReport,
  downloadConsentDocuments,
  downloadPDFReport,
  type DocumentUrlsResponse,
} from "../../legacy-case-details/utils/legacyCaseDownloads";

interface Props {
  caseId: string;
  reportId: string;
  teamType: "HR" | "PM";
  isInDetailsPage?: boolean;
}

type DownloadFunction = (
  documentUrls: DocumentUrlsResponse,
  reportId: string
) => Promise<void>;

interface DownloadConfig {
  downloadFn: DownloadFunction;
  errorMessage: string;
  logMessage: string;
}

const DOWNLOAD_CONFIGS: Record<string, DownloadConfig> = {
  download_pdf: {
    downloadFn: downloadPDFReport,
    errorMessage: "Unable to download report PDF",
    logMessage: "Failed to download PDF",
  },
  download_csv: {
    downloadFn: downloadCSVReport,
    errorMessage: "Unable to download report CSV",
    logMessage: "Failed to download CSV",
  },
  download_consent: {
    downloadFn: downloadConsentDocuments,
    errorMessage: "Unable to download consent form",
    logMessage: "Failed to download consent form",
  },
  download_case_documents: {
    downloadFn: downloadAllDocuments,
    errorMessage: "Unable to download case documents PDF",
    logMessage: "Failed to download case documents",
  },
};

export function LegacyCaseActionsMenu(props: Props) {
  const { reportId, teamType, isInDetailsPage = false } = props;
  const request = useRequest();

  const handleDownload = async (config: DownloadConfig, actionKey: string) => {
    try {
      const response = (await request(
        `/api/internal/client-portal/legacy-case/${reportId}/documents/`
      )) as DocumentUrlsResponse;

      await config.downloadFn(response, reportId);
    } catch (error) {
      console.error(`${config.logMessage}:`, error);
      // Use string literals directly for i18next parser
      if (actionKey === "download_pdf") {
        showToast(t("Unable to download report PDF"), { type: "error" });
      } else if (actionKey === "download_csv") {
        showToast(t("Unable to download report CSV"), { type: "error" });
      } else if (actionKey === "download_consent") {
        showToast(t("Unable to download consent form"), { type: "error" });
      } else {
        showToast(t("Unable to download case documents PDF"), {
          type: "error",
        });
      }
    }
  };

  const handleAction = (key: string) => {
    const config = DOWNLOAD_CONFIGS[key];
    if (config) {
      handleDownload(config, key);
    } else if (key === "open_new_tab") {
      window.open(`/legacy-case-details/${reportId}`, "_blank");
    }
  };

  const menuItemProps = {
    minWidth: "196px" as const,
    textColor: "text-base" as const,
    textStyle: "input/text-field" as const,
  };

  return (
    <DropdownMenu
      triggerButton={<GhostIconButton size="small" icon="more_horiz" />}
      placement="bottom right"
      onItemAction={handleAction}
    >
      <MenuItem id="download_pdf" {...menuItemProps}>
        {t("Download report PDF (.pdf)")}
      </MenuItem>
      {teamType === "HR" && (
        <>
          <MenuItem id="download_csv" {...menuItemProps}>
            {t("Download report CSV (.csv)")}
          </MenuItem>
          <MenuItem id="download_consent" {...menuItemProps}>
            {t("Download consent form PDF (.pdf)")}
          </MenuItem>
        </>
      )}
      {isInDetailsPage ? (
        <MenuItem id="download_case_documents" {...menuItemProps}>
          {t("Download case documents")}
        </MenuItem>
      ) : (
        <MenuItem id="open_new_tab" {...menuItemProps}>
          {t("Open in a new Tab")}
        </MenuItem>
      )}
    </DropdownMenu>
  );
}
